import { computed, onBeforeUnmount, onMounted, reactive, ref, unref, watch } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { get } from 'lodash';

import { useEventHub } from '@satellite/composables/useEventHub';
import yardViewService from '@satellite/services/yard-view-service';

import { DEFAULT_INVALIDATE_OPTIONS, queryKeys } from './queryKeys';
import { spotGridItemToYardSpotDetails } from '../helpers/spotGridItemToYardSpotDetails';

import type { ComputedRef, Ref } from 'vue';
import type { Maybe } from '@nova/core';
import type { YardAreaDetails } from './useYardAreaPresenterV2';
import type { SpotGridArea, SpotGridAssignment, SpotGridItem } from './yard/spotGridTypes';

export type {
  SpotGridArea,
  SpotGridAssignment,
  SpotGridItem,
  SpotGridOpenMoveTaskRef,
  SpotGridReserveRef
} from './yard/spotGridTypes';

export interface UseYardSpotGridPresenterV2Options {
  warehouseId: Ref<Maybe<string>> | ComputedRef<Maybe<string>>;
  headless?: boolean;
  mode?: 'live';
  staleTime?: number;
}

export function useYardSpotGridPresenterV2(options: UseYardSpotGridPresenterV2Options) {
  const { warehouseId, headless = false, mode = 'live', staleTime = Infinity } = options;
  const eventHub = useEventHub();
  const queryClient = useQueryClient();

  const effectiveWarehouseId = computed(() => unref(warehouseId) || null);
  const spotGridQueryKey = computed(() =>
    effectiveWarehouseId.value ? queryKeys.spotGrid(effectiveWarehouseId.value) : []
  );

  const spotsById = reactive(new Map<string, SpotGridItem>());
  const areasById = reactive(new Map<string, SpotGridArea>());
  const spotsByAssetVisitId = reactive(new Map<string, SpotGridItem>());
  const spotsByAssetContainerId = reactive(new Map<string, SpotGridItem>());
  const spotsByAppointmentId = reactive(new Map<string, SpotGridItem>());

  const isInViewport = ref(headless);
  const pendingInvalidation = ref(false);

  const invalidate = () => {
    if (spotGridQueryKey.value.length) {
      queryClient.invalidateQueries(
        { queryKey: spotGridQueryKey.value },
        DEFAULT_INVALIDATE_OPTIONS
      );
    }
  };

  const handleIntersect = (entry?: { isIntersecting?: boolean } | boolean) => {
    if (headless) {
      return;
    }
    const isIntersecting = typeof entry === 'boolean' ? entry : (entry?.isIntersecting ?? true);
    isInViewport.value = isIntersecting;

    if (isIntersecting && pendingInvalidation.value) {
      pendingInvalidation.value = false;
      invalidate();
    }
  };

  const indexSpotAssignment = (item: SpotGridItem, assignment: SpotGridAssignment) => {
    if (assignment.assetVisitId) {
      spotsByAssetVisitId.set(assignment.assetVisitId, item);
    }

    if (assignment.assetContainerId) {
      spotsByAssetContainerId.set(assignment.assetContainerId, item);
    }

    if (assignment.assetVisit?.appointmentId) {
      spotsByAppointmentId.set(assignment.assetVisit.appointmentId, item);
    }
  };

  const buildSupportMaps = (mappedAreas: SpotGridArea[]) => {
    spotsById.clear();
    areasById.clear();
    spotsByAssetVisitId.clear();
    spotsByAssetContainerId.clear();
    spotsByAppointmentId.clear();

    for (const area of mappedAreas) {
      areasById.set(area.id, area);

      for (const item of area.spots) {
        spotsById.set(item.id, item);

        if (Array.isArray(item.spotAssignments)) {
          for (const assignment of item.spotAssignments) {
            indexSpotAssignment(item, assignment);
          }
        }
      }
    }
  };

  const spotGridQuery = useQuery({
    queryKey: spotGridQueryKey,
    queryFn: async ({ signal }) => {
      const warehouseId = effectiveWarehouseId.value;
      if (!warehouseId) {
        return [] as SpotGridArea[];
      }

      const response = await yardViewService.getSpotGrid(warehouseId, {
        requestConfig: { signal },
        enableCache: false
      });

      const areas = get(response, 'data.areas', get(response, 'areas'));
      if (!Array.isArray(areas)) {
        return [] as SpotGridArea[];
      }

      const mappedAreas = areas as SpotGridArea[];

      for (const area of mappedAreas) {
        const yardAreaDetails: YardAreaDetails = {
          id: area.id,
          name: area.name,
          warehouseId: area.warehouseId ?? warehouseId,
          orgId: area.orgId,
          spots: area.spots.map(item => spotGridItemToYardSpotDetails(item, warehouseId))
        };
        queryClient.setQueryData(queryKeys.spotAreaDetail(area.id), yardAreaDetails);
      }

      return mappedAreas;
    },
    enabled: computed(() => Boolean(effectiveWarehouseId.value) && isInViewport.value),
    staleTime,
    refetchOnWindowFocus: false
  });

  watch(
    () => spotGridQuery.data.value,
    areas => {
      buildSupportMaps(Array.isArray(areas) ? areas : []);
    },
    { immediate: true }
  );

  const refresh = async () => {
    await spotGridQuery.refetch();
  };

  const shouldHandleWarehouseScopedEvent = (event: { warehouseId?: Maybe<string> }) => {
    if (!effectiveWarehouseId.value) {
      return false;
    }

    if (event?.warehouseId && event.warehouseId !== effectiveWarehouseId.value) {
      return false;
    }

    return true;
  };

  const handleWarehouseScopedInvalidate = (event: {
    warehouseId?: Maybe<string>;
    id?: string;
    spotId?: string;
  }) => {
    if (!shouldHandleWarehouseScopedEvent(event)) {
      return;
    }

    if (!isInViewport.value) {
      pendingInvalidation.value = true;
      return;
    }

    invalidate();
  };

  const handleSpotAreaEvent = (event: { warehouseId?: Maybe<string>; id?: string }) => {
    if (!shouldHandleWarehouseScopedEvent(event)) {
      return;
    }
    if (!isInViewport.value) {
      pendingInvalidation.value = true;
      return;
    }

    invalidate();
  };

  const handleYardTaskEvent = (event: { warehouseId?: Maybe<string> }) => {
    if (!shouldHandleWarehouseScopedEvent(event)) {
      return;
    }
    if (!isInViewport.value) {
      pendingInvalidation.value = true;
      return;
    }

    invalidate();
  };

  const handleAssetVisitLikeEvent = (event: { warehouseId?: Maybe<string> }) => {
    if (!shouldHandleWarehouseScopedEvent(event)) {
      return;
    }
    if (!isInViewport.value) {
      pendingInvalidation.value = true;
      return;
    }

    invalidate();
  };

  watch(effectiveWarehouseId, (newId, oldId) => {
    if (newId !== oldId) {
      pendingInvalidation.value = false;
    }
  });

  const data = computed<SpotGridArea[]>(() => spotGridQuery.data.value ?? []);

  const getSpotById = (spotId: string): SpotGridItem | null => spotsById.get(spotId) ?? null;
  const getAreaById = (areaId: string): SpotGridArea | null => areasById.get(areaId) ?? null;

  /**
   * Spots of one area, in the order the BFF sorted them (`sortSpots`, server-side).
   *
   * `excludeInactive` is opt-in because callers legitimately differ: task forms count only spots a
   * driver can be sent to, while the registration screen lists the area as it is. The endpoint does
   * not filter on `isActive`, so whichever a caller wants has to be said here.
   */
  const getSpotsByAreaId = (
    areaId: string,
    options: { excludeInactive?: boolean } = {}
  ): SpotGridItem[] => {
    const spots = getAreaById(areaId)?.spots ?? [];

    return options.excludeInactive ? spots.filter(spot => spot.isActive) : spots;
  };

  const getSpotByAssetVisitId = (assetVisitId: string): SpotGridItem | null => {
    return spotsByAssetVisitId.get(assetVisitId) ?? null;
  };

  const getSpotByAssetContainerId = (assetContainerId: string): SpotGridItem | null => {
    return spotsByAssetContainerId.get(assetContainerId) ?? null;
  };

  const getSpotByAppointmentId = (appointmentId: string): SpotGridItem | null => {
    return spotsByAppointmentId.get(appointmentId) ?? null;
  };

  const isLoading = computed(() => spotGridQuery.isLoading.value);
  const isFetching = computed(() => spotGridQuery.isFetching.value);
  const error = computed(() => spotGridQuery.error.value);

  onMounted(() => {
    if (mode !== 'live') {
      return;
    }

    /**
     * TODO: (welington) we should fine tune these and evaluate updating the cache for this
     * endpoint using the useYardSpotPresenterV2 query result.
     */

    eventHub.on('create-Spot', handleWarehouseScopedInvalidate);
    eventHub.on('update-Spot', handleWarehouseScopedInvalidate);
    eventHub.on('delete-Spot', handleWarehouseScopedInvalidate);

    eventHub.on('create-SpotArea', handleSpotAreaEvent);
    eventHub.on('update-SpotArea', handleSpotAreaEvent);
    eventHub.on('delete-SpotArea', handleSpotAreaEvent);

    eventHub.on('create-SpotAssignment', handleWarehouseScopedInvalidate);
    eventHub.on('update-SpotAssignment', handleWarehouseScopedInvalidate);
    eventHub.on('delete-SpotAssignment', handleWarehouseScopedInvalidate);

    eventHub.on('create-YardTask', handleYardTaskEvent);
    eventHub.on('update-YardTask', handleYardTaskEvent);
    eventHub.on('delete-YardTask', handleYardTaskEvent);

    eventHub.on('create-AssetVisit', handleAssetVisitLikeEvent);
    eventHub.on('update-AssetVisit', handleAssetVisitLikeEvent);
    eventHub.on('delete-AssetVisit', handleAssetVisitLikeEvent);

    eventHub.on('create-AssetContainer', handleAssetVisitLikeEvent);
    eventHub.on('update-AssetContainer', handleAssetVisitLikeEvent);
    eventHub.on('delete-AssetContainer', handleAssetVisitLikeEvent);
  });

  onBeforeUnmount(() => {
    if (mode !== 'live') {
      return;
    }

    eventHub.off('create-Spot', handleWarehouseScopedInvalidate);
    eventHub.off('update-Spot', handleWarehouseScopedInvalidate);
    eventHub.off('delete-Spot', handleWarehouseScopedInvalidate);

    eventHub.off('create-SpotArea', handleSpotAreaEvent);
    eventHub.off('update-SpotArea', handleSpotAreaEvent);
    eventHub.off('delete-SpotArea', handleSpotAreaEvent);

    eventHub.off('create-SpotAssignment', handleWarehouseScopedInvalidate);
    eventHub.off('update-SpotAssignment', handleWarehouseScopedInvalidate);
    eventHub.off('delete-SpotAssignment', handleWarehouseScopedInvalidate);

    eventHub.off('create-YardTask', handleYardTaskEvent);
    eventHub.off('update-YardTask', handleYardTaskEvent);
    eventHub.off('delete-YardTask', handleYardTaskEvent);

    eventHub.off('create-AssetVisit', handleAssetVisitLikeEvent);
    eventHub.off('update-AssetVisit', handleAssetVisitLikeEvent);
    eventHub.off('delete-AssetVisit', handleAssetVisitLikeEvent);

    eventHub.off('create-AssetContainer', handleAssetVisitLikeEvent);
    eventHub.off('update-AssetContainer', handleAssetVisitLikeEvent);
    eventHub.off('delete-AssetContainer', handleAssetVisitLikeEvent);
  });

  return {
    data,
    isLoading,
    isFetching,
    error,

    refresh,
    invalidate,

    getSpotById,
    getAreaById,
    getSpotsByAreaId,
    getSpotByAssetVisitId,
    getSpotByAssetContainerId,
    getSpotByAppointmentId,

    onIntersect: handleIntersect,
    query: spotGridQuery
  };
}
